"use client";

import { useMemo, useState, useEffect } from "react";
import { PortableText } from "next-sanity";
import GridColumn from "../common/grid-column/grid-column";
import GridRow from "../common/grid-row/grid-row";
import { LegalPageData } from "@/sanity/interfaces";
import Image from "@/components/common/image/image";
import BaseLink from "../common/base-link/base-link";
import TableRenderer from "@/components/common/sanity/table-renderer";
import RichTableRenderer from "@/components/common/sanity/rich-table-renderer/rich-table-renderer";
import InternalLink from "@/components/common/internal-link";
import imageUrl from "@/sanity/utilities/imageUrl";
import DateString, {
  DateStringFormat,
} from "../common/date-string/date-string";

interface ILegalProps {
  data: LegalPageData;
}

const Legal = ({ data }: ILegalProps) => {
  const { contact, downloads, effectiveFrom, lastUpdated, sections, title } =
    data;
  const [open, setOpen] = useState(false);

  const tableOfContents = useMemo(
    () =>
      (sections
        ?.map((section) =>
          section.heading ? { id: section._key, label: section.heading } : null,
        )
        .filter((s) => s !== null) || []) as { id: string; label: string }[],
    [sections],
  );

  const [activeId, setActiveId] = useState(
    tableOfContents.length > 0 ? tableOfContents[0].id : "",
  );

  // PortableText Components for styling Sanity blocks
  const components = {
    types: {
      undefined: () => null,
      table: ({ value }: { value: any }) => <TableRenderer value={value} />,
      richTable: ({ value }: { value: any }) => (
        <RichTableRenderer value={value} />
      ),
      image: ({ value }: { value: any }) => (
        <Image
          src={imageUrl(value.asset).url() ?? ""}
          alt={value.alt}
          width={value.width}
          height={value.height}
          style={{
            display: value.display,
            width: value.width,
            height: value.height,
          }}
        />
      ),
    },
    block: {
      normal: ({ children }: any) => (
        <p className="text-[16px] leading-[26px] text-[#373A36] md:text-[18px] md:leading-[28px] lg:text-[18px]">
          {children}
        </p>
      ),
    },

    list: {
      bullet: ({ children }: any) => <ul>{children}</ul>,
      number: ({ children }: any) => (
        <ol className="list-decimal pl-10 text-[16px] leading-[26px] text-[#373A36] md:text-[18px] md:leading-[28px]">
          {children}
        </ol>
      ),
    },
    listItem: {
      bullet: ({ children }: any) => (
        <li className="relative mb-2 pl-10 text-[16px] leading-[26px] text-[#373A36] md:text-[18px] md:leading-[28px]">
          <Image
            src="/checkmark.svg"
            alt="Checkmark"
            className="absolute left-0 top-1"
            width={24}
            height={24}
          />
          {children}
        </li>
      ),
      number: ({ children }: any) => <li className="mb-2">{children}</li>,
    },
    marks: {
      unknownMark: () => null,
      internalLink: ({ children, value }: any) => (
        <InternalLink value={value}>{children}</InternalLink>
      ),
      link: ({ children, value }: any) => (
        <BaseLink
          href={value?.href || "#"}
          target={value?.blank ? "_blank" : undefined}
          rel={value?.blank ? "noopener noreferrer" : undefined}
          className="underline"
        >
          {children}
        </BaseLink>
      ),
    },
  };

  const content = useMemo(
    () =>
      sections?.map((section) => (
        <section id={section._key} key={section._key}>
          {section.heading && (
            <div className="mb-5">
              <h2 className="text-[24px] font-semibold leading-[28px] text-[#373A36]">
                {section.heading || "Section"}
              </h2>
            </div>
          )}
          <div className="flex flex-col gap-10">
            <PortableText value={section?.body || []} components={components} />
          </div>
        </section>
      )),
    [sections],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        let closestEntry: any = null;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (
              !closestEntry ||
              entry.boundingClientRect.top < closestEntry.boundingClientRect.top
            ) {
              closestEntry = entry;
            }
          }
        });
        if (closestEntry) {
          setActiveId(closestEntry.target.id);
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -50% 0px" },
    );

    sections?.forEach((section) => {
      const element = document.getElementById(section._key);
      if (element) observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };
  }, [sections]);

  useEffect(() => {
    if (activeId === "" && sections && sections.length > 0) {
      setActiveId(sections[0]._key);
    }
  }, [sections, activeId]);

  return (
    <div className="pb-10 pt-5 text-[#373a36]">
      <GridRow>
        <GridColumn className="lg:mt-15 mx-auto mt-1 px-4 lg:mt-10">
          {/* Mobile Dropdown Navigation */}
          <div className="sticky top-[0] z-[101] bg-white py-4 lg:hidden">
            <div className="relative w-full lg:hidden">
              <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full rounded-md border border-[#4A4A4A] bg-white px-4 py-3 pr-10 text-left text-[#373A36]"
              >
                {tableOfContents.find((s) => s.id === activeId)?.label}
              </button>

              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <Image
                  src="/down-arrow.svg"
                  alt="dropdown arrow"
                  width={24}
                  height={24}
                />
              </div>

              {open && (
                <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-[#ddd] bg-white shadow">
                  {tableOfContents.map((s) => (
                    <li
                      key={s.id}
                      onClick={() => {
                        setActiveId(s.id);
                        setOpen(false);
                        const el = document.getElementById(s.id);
                        if (el) {
                          window.scrollTo({
                            top: el.offsetTop - 100,
                            behavior: "smooth",
                          });
                        }
                      }}
                      className={`cursor-pointer whitespace-normal break-words px-4 py-2 text-[#373A36] ${
                        activeId === s.id ? "font-semibold" : ""
                      }`}
                    >
                      {s.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="grid grid-cols-12">
            {/* Desktop Sidebar */}
            <div className="col-span-3 hidden lg:block">
              <nav className="sticky top-5 hidden rounded-lg bg-white shadow-[5px_6px_20px_rgba(0,0,0,0.07)] lg:block">
                <ul>
                  {tableOfContents.map((s) => (
                    <li
                      key={s.id}
                      className="h-15 flex cursor-pointer items-center border border-l-0 border-r-0 border-t-0 border-[#e9e9e9] py-4 pr-5 last:!border-b-0"
                    >
                      <a
                        href={`#${s.id}`}
                        className={`pl-3 text-[16px] font-medium ${
                          activeId === s.id
                            ? "rounded border-l-4 !border-[#069590]"
                            : "border-l-0"
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          const el = document.getElementById(s.id);
                          if (el) {
                            window.scrollTo({
                              top: el.offsetTop - 100,
                              behavior: "smooth",
                            });
                          }
                          setActiveId(s.id);
                        }}
                      >
                        {s.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
            {/* Main Content */}
            <div className="col-span-12 mx-auto w-full max-w-[1200px] overflow-hidden lg:col-span-9 lg:pl-[calc(100%/12)]">
              <div className="flex flex-col gap-10">
                <div>
                  {title && (
                    <h1 className="font-[lora] text-[28px] font-semibold leading-[38px] tracking-[0.2px] text-[#00698F] md:text-[36px] md:leading-[48px]">
                      {title}
                    </h1>
                  )}
                  {effectiveFrom && (
                    <p className="mt-2 text-[18px] font-medium not-italic leading-[26px] text-[#373A36]">
                      Effective Date:{" "}
                      <DateString
                        date={effectiveFrom}
                        format={DateStringFormat.SLASHES}
                        hasTime={false}
                      />
                    </p>
                  )}
                </div>
                {content}
                {/* Contact Section */}
                {contact?.org != " " && (
                  <section id="contact">
                    <div className="mb-10 flex max-w-[460px] flex-col gap-3 rounded-md border border-[#e3e6ea] bg-[#f6f6f6] p-4">
                      <div className="flex flex-row gap-2">
                        <div className="mt-0.5 text-[#7a8696]">
                          <BaseLink href={contact?.mapLink ?? "#"}>
                            <Image
                              src="/map-pin.svg"
                              alt="location-icon"
                              width={24}
                              height={24}
                            />
                          </BaseLink>
                        </div>
                        <span className="font-semibold">
                          Our Contact Information
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <address className="flex flex-col pl-8 font-medium text-[#373a36]">
                          <span>{contact?.org}</span>
                          {contact?.addressLines?.map((line, i) => (
                            <span key={i}>{line}</span>
                          ))}
                          <span>
                            {contact?.city}, {contact?.region}{" "}
                            {contact?.postalCode}
                          </span>
                        </address>
                      </div>
                    </div>
                    <div>
                      <p className="text-[16px] leading-7 text-[#373a36] md:text-[18px]">
                        AmeriSave reserves the right to change its practices
                        concerning information gathered from visitors to its
                        websites. If such changes occur, this Privacy Policy
                        will be updated. Visitors should check back periodically
                        to confirm the then-current terms of this Policy. This
                        Privacy Policy is not intended to and does not create
                        any contractual or other legal rights in or on behalf of
                        any party.
                      </p>
                    </div>
                    {lastUpdated && (
                      <div className="mt-10 text-[18px] font-medium">
                        Last updated:{" "}
                        <DateString
                          date={lastUpdated}
                          format={DateStringFormat.SLASHES}
                          hasTime={false}
                        />
                      </div>
                    )}
                  </section>
                )}

                {/* Downloads Section */}
                {!!downloads?.length && (
                  <section id="documents" className="flex flex-col gap-10">
                    {downloads?.map((download, index) => (
                      <div
                        key={download._key}
                        className="mb-3 flex flex-col items-start gap-3 rounded-md bg-[#F6F6F6] p-6"
                      >
                        <div className="mt-0.5 flex h-6 w-6 items-center justify-center text-[#7a8696]">
                          <Image
                            src="/document.svg"
                            alt="document"
                            width={24}
                            height={24}
                          />
                        </div>
                        <div className="flex flex-col gap-2 text-[#394650]">
                          <p className="text-[16px] font-medium not-italic leading-[28px] text-[#373A36] md:text-[18px]">
                            {download.label}
                          </p>

                          <a
                            className="inline-flex w-fit items-center gap-2 rounded-md bg-[#00698F] px-6 py-3 text-[14px] font-semibold text-[#FFFFFF] transition visited:!text-[#FFFFFF] md:text-[18px]"
                            href={
                              download?.file === null
                                ? (download?.url ?? "#")
                                : (download.file?.asset?.url ?? "#")
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {download.linkLabel}
                            <Image
                              src="/download-icon.svg"
                              alt="Download icon"
                              width={32}
                              height={32}
                            />
                          </a>
                        </div>
                      </div>
                    ))}
                  </section>
                )}
              </div>
            </div>
          </div>
        </GridColumn>
      </GridRow>
    </div>
  );
};

export default Legal;
